import { createContext, useContext, type ReactNode } from 'react'
import { useChat, type UseChatResult } from './useChat'
import { foundryTransport } from './foundryTransport'
import { claudeTransport } from './claudeTransport'

/**
 * Lifts `useChat` above the router so the chat state — messages, thread id,
 * status, in-flight SSE stream, trusted-tools set, last-user-message ref —
 * survives every route change inside the authenticated shell. ChatView
 * reads from this context instead of calling the hook itself; navigating
 * away and back is now an O(0) state transition, not a re-init.
 *
 * **Scope deliberately limited to the tab's lifetime.** This is in-
 * memory only; closing the tab still loses the conversation. We
 * explicitly avoid `localStorage` / `sessionStorage` because the
 * earlier persistence attempt only persisted `threadId` (not the
 * messages list) and produced a context-vs-UI asymmetry that led to
 * hallucinations — see the comment block in `useChat.ts`.
 *
 * **Transport choice lives here.** `VITE_CHAT_BACKEND=claude` routes the
 * chat through Claude-on-Foundry (Messages API, ./claudeTransport.ts);
 * unset or any other value keeps the Foundry Agent Service (gpt-4o,
 * Responses API). Baked at build time by Vite, so flipping backends is a
 * workflow env change + redeploy — never a code change. The reducer and
 * the render layer are identical under both.
 *
 * **Where this gets mounted.** Inside `AuthenticatedShell` in
 * `App.tsx`, *above* the `<Switch>`. That means the provider is only
 * alive when authState is `authenticated` AND the user has the Reader
 * role — exactly when the chat surface is available.
 */

const transport =
  import.meta.env.VITE_CHAT_BACKEND === 'claude' ? claudeTransport : foundryTransport

const ChatContext = createContext<UseChatResult | null>(null)

export function ChatProvider({ children }: { children: ReactNode }) {
  // Single call to useChat for the whole authenticated session. Its
  // useEffects (auto-approve trusted tools, auto-dispatch on
  // tool_outputs_required) keep running while the user is on other
  // routes, so an SSE stream kicked off on /chat can finish writing
  // its answer while the user is browsing Initiatives — they'll see
  // the completed bubble when they navigate back.
  const chat = useChat(transport)
  return <ChatContext.Provider value={chat}>{children}</ChatContext.Provider>
}

export function useChatContext(): UseChatResult {
  const ctx = useContext(ChatContext)
  if (!ctx) {
    throw new Error(
      'useChatContext must be used inside a <ChatProvider>. ' +
        'Mount ChatProvider above your routes in App.tsx.',
    )
  }
  return ctx
}

/**
 * A conversation is "active" — and chat focus mode is on — once the thread
 * has a message OR a run is in flight (`status !== idle`). The status clause
 * matters: `send()` flips status to `sending` synchronously, before the user
 * bubble commits, so arriving on `/chat` from a home/other-page ask renders
 * already-focused with no hero flash. Single source of truth for both the
 * hero collapse (ChatView) and the footer collapse (App shell).
 * See docs/sps/chat-focus-mode-requirements.md (R1.1).
 */
export function chatIsActive(chat: Pick<UseChatResult, 'messages' | 'status'>): boolean {
  return chat.messages.length > 0 || chat.status.kind !== 'idle'
}
